// SPDX-License-Identifier: GPL-3.0-or-later
// Draw-cost helpers of the code-built characters (own code).
// A SigmaBot is made of many small primitives. Each mesh is one draw call, and each material is
// one more object for the renderer to track. Two helpers cut that cost without a visible change:
// `shareMaterials` makes meshes of the same colour use one material, and `mergeStatic` bakes the
// static meshes of one node into one mesh for each material.
// Rules: no clock, no random, no DOM. The same tree gives the same result.

import {
  type BufferGeometry,
  type Material,
  Matrix4,
  Mesh,
  type MeshBasicMaterial,
  type MeshStandardMaterial,
  type Object3D,
} from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { OUTLINE_KEY } from "./common.ts";

type Paint = MeshStandardMaterial | MeshBasicMaterial;

/** True when the object is a mesh with one plain material (not a list of materials). */
function isPlainMesh(o: Object3D): o is Mesh<BufferGeometry, Paint> {
  const m = o as Mesh;
  return m.isMesh === true && !Array.isArray(m.material);
}

/** The key of a fresh material: its kind and every setting that a build can change. */
function materialKey(m: Paint): string {
  return [m.type, m.color.getHex(), m.side, m.transparent ? 1 : 0, m.opacity].join("/");
}

/**
 * Make the meshes of one tree that have the same material key use one material. The first one
 * stays. The others are freed. Call it right after the build, before any code changes a material
 * (a character may change the opacity of its materials later).
 */
export function shareMaterials(root: Object3D): void {
  const first = new Map<string, Paint>();
  root.traverse((o) => {
    if (!isPlainMesh(o)) return;
    const key = materialKey(o.material);
    const known = first.get(key);
    if (!known) first.set(key, o.material);
    else if (known !== o.material) {
      o.material.dispose();
      o.material = known;
    }
  });
}

/** The matrix of a mesh in the space of `target`. The meshes have no matrix update yet. */
function matrixIn(target: Object3D, mesh: Object3D): Matrix4 {
  const m = new Matrix4();
  for (let o: Object3D | null = mesh; o && o !== target; o = o.parent) {
    o.updateMatrix();
    m.premultiply(o.matrix);
  }
  return m;
}

/** A copy of the geometry in the space of `target`, with only the attributes that all parts share. */
function bakedCopy(mesh: Mesh<BufferGeometry, Paint>, target: Object3D): BufferGeometry {
  const g = mesh.geometry.clone();
  g.deleteAttribute("uv");
  g.clearGroups();
  if (!g.index) {
    const count = g.getAttribute("position").count;
    g.setIndex(Array.from({ length: count }, (_, i) => i));
  }
  g.applyMatrix4(matrixIn(target, mesh));
  return g;
}

/** A mesh can join a merge when it is not an outline hull and does not mirror its shape. */
function canMerge(target: Object3D, o: Object3D): o is Mesh<BufferGeometry, Paint> {
  if (!isPlainMesh(o) || o.userData[OUTLINE_KEY] === true) return false;
  return o.children.length === 0 && matrixIn(target, o).determinant() > 0;
}

/**
 * Merge the static meshes under `target` that share one material into one mesh each. The new
 * meshes are children of `target`, in the order of the first mesh of each material. A material
 * with one mesh stays as it is. The nodes of the tree stay, so a name lookup still works. Call
 * `shareMaterials` first. Do not use it on a node that code moves part by part.
 */
export function mergeStatic(target: Object3D, name: string): void {
  const groups = new Map<Material, Mesh<BufferGeometry, Paint>[]>();
  target.traverse((o) => {
    if (o === target || !canMerge(target, o)) return;
    const list = groups.get(o.material);
    if (list) list.push(o);
    else groups.set(o.material, [o]);
  });
  let index = 0;
  for (const [material, meshes] of groups) {
    if (meshes.length < 2) continue;
    const merged = mergeGeometries(meshes.map((m) => bakedCopy(m, target)));
    if (!merged) continue;
    merged.computeBoundingBox();
    for (const m of meshes) {
      m.removeFromParent();
      m.geometry.dispose();
    }
    const mesh = new Mesh(merged, material);
    mesh.name = `${name}Merged${index++}`;
    target.add(mesh);
  }
}
